import {
  mysqlTable,
  int,
  bigint,
  varchar,
  text,
  double,
  date,
  boolean,
  timestamp,
} from "drizzle-orm/mysql-core";
import { companies } from "./tenancy";
import { users } from "./identity";
import { teams, designations } from "./hr";

/**
 * employee_details — the HR record for a user (1:1 with users). `reporting_to` is the
 * approval-routing manager (reskin-docs/07 §2); `added_by`/`user_id` drive RBAC ownership
 * scopes (reskin-docs/03 §1). Ported line-by-line from the base migration L863–889.
 *
 * Deferred cross-batch FKs: department_id → teams · designation_id → designations (Batch 1.2 HR).
 */
export const employeeDetails = mysqlTable("employee_details", {
  id: int("id", { unsigned: true }).autoincrement().primaryKey(),
  companyId: int("company_id", { unsigned: true }).references(() => companies.id, {
    onDelete: "cascade",
    onUpdate: "cascade",
  }),
  userId: int("user_id", { unsigned: true })
    .notNull()
    .references(() => users.id, { onDelete: "cascade", onUpdate: "cascade" }),
  employeeId: varchar("employee_id", { length: 255 }).unique(),
  address: text("address"),
  hourlyRate: double("hourly_rate"),
  slackUsername: varchar("slack_username", { length: 255 }).unique(),
  departmentId: int("department_id", { unsigned: true }).references(() => teams.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  designationId: bigint("designation_id", { mode: "number", unsigned: true }).references(
    () => designations.id,
    { onDelete: "cascade", onUpdate: "cascade" },
  ),
  joiningDate: timestamp("joining_date").defaultNow().notNull(),
  lastDate: date("last_date"),
  addedBy: int("added_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  lastUpdatedBy: int("last_updated_by", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  attendanceReminder: date("attendance_reminder"),
  dateOfBirth: date("date_of_birth"),
  calendarView: text("calendar_view"),
  aboutMe: text("about_me"),
  reportingTo: int("reporting_to", { unsigned: true }).references(() => users.id, {
    onDelete: "set null",
    onUpdate: "cascade",
  }),
  // 2022_12_13 add_new_fields_in_employee_details_table
  contractEndDate: date("contract_end_date"),
  internshipEndDate: date("internship_end_date"),
  employmentType: varchar("employment_type", { length: 255 }),
  marriageAnniversaryDate: date("marriage_anniversary_date"),
  // marital_status: added nullable (2022_12_13); 2023_05_02 + 2024_01_11 ->default('single')->change()
  // final change() drops nullable → NOT NULL DEFAULT 'single'
  maritalStatus: varchar("marital_status", { length: 255 }).default("single").notNull(),
  noticePeriodEndDate: date("notice_period_end_date"),
  noticePeriodStartDate: date("notice_period_start_date"),
  probationEndDate: date("probation_end_date"),
  // 2024_06_03 add_company_address_id_to_employee_details_table (onDelete set null)
  companyAddressId: bigint("company_address_id", { mode: "number", unsigned: true }), // FK → company_addresses (deferred)
  // 2024_07_05 (Payroll) add_hourly_rate_column — overtime calculation only
  overtimeHourlyRate: double("overtime_hourly_rate", { precision: 16, scale: 2 }),
  // 2026_06_18 create_payroll_entities_and_tag_employees
  payrollEntityId: int("payroll_entity_id", { unsigned: true }), // FK → company_payroll_entities (deferred)
  payrollCategory: varchar("payroll_category", { length: 30 }).default("permanent"),
  onPayroll: boolean("on_payroll").default(true),
  createdAt: timestamp("created_at"),
  updatedAt: timestamp("updated_at"),
});
